import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import SongSearchModal from './SongSearchModal';
import AuditionSongService from '../../../services/auditionSongService';

const VoiceTypeCard = ({ 
  voiceType, 
  auditionCategories, 
  auditionSongs, 
  isExpanded, 
  onToggle, 
  onSongAssigned, 
  onSongRemoved, 
  user, 
  onError 
}) => {
  const [showSongSearch, setShowSongSearch] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleAddSong = (category) => {
    setSelectedCategory(category);
    setShowSongSearch(true);
  };

  const handleSongSelected = async (song, characterRole) => {
    try {
      setIsLoading(true);
      onError?.(null);

      const { data, error } = await AuditionSongService?.createAuditionSong({
        song_id: song?.id,
        voice_type: voiceType?.id,
        category: selectedCategory?.id,
        created_by: user?.id
      });

      if (error) {
        onError?.(`Failed to assign song: ${error}`);
        return;
      }

      // Success - close modal and refresh parent
      setShowSongSearch(false);
      setSelectedCategory(null);
      onSongAssigned?.();
    } catch (err) {
      onError?.('An unexpected error occurred while assigning the song.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveSong = async (auditionSongId) => {
    try {
      setIsLoading(true);
      onError?.(null);

      const { error } = await AuditionSongService?.deleteAuditionSong(auditionSongId);

      if (error) {
        onError?.(`Failed to remove song: ${error}`);
        return;
      }

      // Success - refresh parent
      onSongRemoved?.();
    } catch (err) {
      onError?.('An unexpected error occurred while removing the song.');
    } finally {
      setIsLoading(false);
    }
  };

  const getSongsForCategory = (categoryId) => {
    return auditionSongs?.filter(song => song?.category === categoryId) || [];
  };

  const getTotalSongsCount = () => {
    return auditionSongs?.length || 0;
  };

  return (
    <>
      <div className="bg-white bg-opacity-10 backdrop-blur-sm border border-white border-opacity-20 rounded-xl overflow-hidden">
        {/* Voice Type Header */}
        <div className="p-6">
          <button
            onClick={onToggle}
            className="w-full flex items-center justify-between hover:bg-white hover:bg-opacity-10 -m-2 p-2 rounded-lg transition-colors duration-200"
          >
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center">
                <Icon name={voiceType?.icon} size={20} className="text-white" />
              </div>
              <div className="text-left">
                <h3 className="text-lg font-heading font-semibold text-white">
                  {voiceType?.label}
                </h3>
                <p className="text-blue-200 text-sm font-body">
                  {getTotalSongsCount()} songs assigned
                </p>
              </div>
            </div>
            <Icon 
              name={isExpanded ? 'ChevronUp' : 'ChevronDown'} 
              size={20} 
              className="text-blue-200" 
            />
          </button>
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-t border-white border-opacity-20">
            <div className="p-6 space-y-4">
              {auditionCategories?.map((category) => {
                const categorySongs = getSongsForCategory(category?.id);
                const canAddMore = categorySongs?.length < 5;

                return (
                  <div key={category?.id} className="bg-white bg-opacity-5 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center space-x-2">
                        <Icon name="Music4" size={16} className="text-blue-300" />
                        <h4 className="font-medium text-white font-caption">
                          {category?.label}
                        </h4>
                        <span className="text-xs text-blue-200 bg-blue-900 bg-opacity-50 px-2 py-1 rounded-full">
                          {categorySongs?.length}/5
                        </span>
                      </div>
                      
                      {canAddMore && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleAddSong(category)}
                          iconName="Plus"
                          iconPosition="left"
                          disabled={isLoading}
                          className="text-blue-300 hover:text-white hover:bg-blue-600 rounded-lg"
                        >
                          Add Song
                        </Button>
                      )}
                    </div>

                    {/* Song List */}
                    <div className="space-y-2">
                      {categorySongs?.length === 0 ? (
                        <div className="text-center py-4 text-blue-200 text-sm">
                          No songs assigned yet
                        </div>
                      ) : (
                        categorySongs?.map((auditionSong, index) => (
                          <div key={auditionSong?.id} className="bg-white bg-opacity-10 rounded-lg p-3">
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-2">
                                  <span className="text-xs text-blue-300 bg-blue-900 bg-opacity-50 px-2 py-1 rounded-full">
                                    {index + 1}
                                  </span>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-white font-medium text-sm truncate">
                                      {auditionSong?.song?.title}
                                    </p>
                                    <p className="text-blue-200 text-xs truncate">
                                      {auditionSong?.song?.musical?.title} • {auditionSong?.song?.musical?.composer}
                                    </p>
                                  </div>
                                </div>
                              </div>
                              
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveSong(auditionSong?.id)}
                                iconName="X"
                                disabled={isLoading}
                                className="text-red-400 hover:text-white hover:bg-red-600 rounded-lg ml-2"
                              />
                            </div>
                            
                            {auditionSong?.difficulty_notes && (
                              <div className="mt-2 text-xs text-blue-200">
                                <Icon name="Info" size={12} className="inline mr-1" />
                                {auditionSong?.difficulty_notes}
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>

                    {!canAddMore && (
                      <div className="mt-2 text-xs text-amber-300 text-center">
                        Maximum of 5 songs reached
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Song Search Modal */}
      {showSongSearch && (
        <SongSearchModal
          isOpen={showSongSearch}
          onClose={() => {
            setShowSongSearch(false);
            setSelectedCategory(null);
          }}
          onSongSelected={handleSongSelected}
          voiceType={voiceType}
          category={selectedCategory}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export default VoiceTypeCard;